import React, { useState } from 'react'
import { DollarSign } from 'lucide-react'

interface TipSelectorProps {
  subtotal: number
  currentTip: number
  onTipChange: (amount: number) => void
}

const TipSelector: React.FC<TipSelectorProps> = ({
  subtotal,
  currentTip,
  onTipChange,
}) => {
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customValue, setCustomValue] = useState('')

  const tipOptions = [
    { percentage: 0.15, label: '15%' },
    { percentage: 0.20, label: '20%' },
    { percentage: 0.25, label: '25%' },
  ]

  const calculateTipAmount = (percentage: number) => {
    return (subtotal * percentage).toFixed(2)
  }

  const handlePercentageClick = (percentage: number) => {
    const amount = Math.round(subtotal * percentage * 100) / 100
    onTipChange(amount)
    setShowCustomInput(false)
  }

  const handleCustomSubmit = () => {
    const amount = parseFloat(customValue)
    if (!isNaN(amount) && amount >= 0) {
      onTipChange(amount)
      setShowCustomInput(false)
      setCustomValue('')
    }
  }

  const handleCancelCustom = () => {
    setShowCustomInput(false)
    setCustomValue('')
  }

  const handleClearTip = () => {
    onTipChange(0)
    setShowCustomInput(false)
    setCustomValue('')
  }

  // Determine if a percentage button is selected
  const isPercentageSelected = (percentage: number) => {
    const expectedAmount = Math.round(subtotal * percentage * 100) / 100
    return Math.abs(currentTip - expectedAmount) < 0.01 && !showCustomInput
  }

  // Check if custom amount is set
  const isCustomSelected = () => {
    if (currentTip === 0) return false
    return !tipOptions.some(opt => isPercentageSelected(opt.percentage))
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8 shadow-lg">
      <div className="flex items-center space-x-2 mb-4">
        <DollarSign className="text-maroon-800" size={20} />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add a Tip</h3>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Tips are optional but help us pay our merchant fees.
      </p>

      {/* Percentage Options */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {tipOptions.map((option) => {
          const isSelected = isPercentageSelected(option.percentage)
          const tipAmount = calculateTipAmount(option.percentage)

          return (
            <button
              key={option.percentage}
              onClick={() => handlePercentageClick(option.percentage)}
              className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-[#8B3A3A]'
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#8B3A3A]'
              }`}
              style={isSelected ? { backgroundColor: '#8B3A3A' } : undefined}
            >
              <span
                className={`text-base font-bold ${isSelected ? '!text-white' : 'text-gray-900 dark:text-gray-100'}`}
              >
                {option.label}
              </span>
              <span
                className={`text-xs mt-1 ${isSelected ? '!text-white' : 'text-gray-500 dark:text-gray-400'}`}
              >
                ${tipAmount}
              </span>
            </button>
          )
        })}
      </div>

      {/* Custom Tip Option */}
      {!showCustomInput ? (
        <button
          onClick={() => setShowCustomInput(true)}
          className={`w-full py-3 px-4 rounded-lg border-2 transition-all ${
            isCustomSelected()
              ? 'border-maroon-800 bg-maroon-50 dark:bg-maroon-900/20 text-maroon-800 dark:text-maroon-400 font-semibold'
              : 'border-gray-200 dark:border-gray-700 hover:border-maroon-400 text-gray-700 dark:text-gray-300'
          }`}
        >
          {isCustomSelected()
            ? `Custom Tip: $${currentTip.toFixed(2)}`
            : 'Custom Amount'}
        </button>
      ) : (
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[120px] relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-semibold">
              $
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCustomSubmit()
                }
              }}
              className="w-full pl-8 pr-3 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-maroon-800 focus:outline-none bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              autoFocus
            />
          </div>
          <button
            onClick={handleCustomSubmit}
            className="px-6 py-3 rounded-lg transition-colors font-semibold"
            style={{ backgroundColor: '#8B3A3A', color: 'white' }}
          >
            Add
          </button>
          <button
            onClick={handleCancelCustom}
            className="px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          {currentTip > 0 && (
            <button
              onClick={handleClearTip}
              className="px-4 py-3 border-2 border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default TipSelector
